import React, { useState } from "react";
import useStyles from "../../components/Common/styles";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toastr } from "../../components/Common/toastr";
import { Blade, BladeValidationDisplay, BladeTabPanel } from "../../components/Common/Blade";
import { TextField, Tab, Tabs, Grid, Typography, Button } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import SaveIcon from "@material-ui/icons/Save";
import { useForm } from "react-hook-form";
import useProtectApi from "../../hooks/useProtectApi";
import { useFetchDocument } from "../../hooks/useFetchEntity";
import { DeleteButton } from "../../components/Common/Buttons";
import { useFetchCountries } from "../../hooks/useFetchEntities";

const DocumentManage = () => {
	const [value, setValue] = React.useState(0);
	const [localDocument] = useFetchDocument();

	const handleChange = (event, newValue) => {
		setValue(newValue);
	};

	function a11yProps(index) {
		return {
			id: `tab-${index}`,
			"aria-controls": `tabpanel-${index}`,
		};
	}
	return !localDocument ? (
		<div></div>
	) : (
		<Blade>
			<Tabs
				value={value}
				onChange={handleChange}
				aria-label="user tabs"
				variant="fullWidth"
				indicatorColor="primary">
				<Tab label="Details" {...a11yProps(0)} />
				<Tab label="Remove" {...a11yProps(1)} />
			</Tabs>
			<BladeTabPanel value={value} index={0}>
				<DocumentDetails document={localDocument} />
			</BladeTabPanel>
			<BladeTabPanel value={value} index={1}>
				<RemoveDocument document={localDocument} />
			</BladeTabPanel>
		</Blade>
	);
};

const DocumentDetails = ({ document }) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const modifyDocumentApi = useProtectApi("document/modify");
	const { register, handleSubmit, errors } = useForm();
	const history = useHistory();

	const [countryNames] = useFetchCountries();

	const [isBusy, setIsBusy] = useState(false);
	const [apiErrors, setApiErrors] = useState();

	const onSubmit = async (data) => {
		var countryId = data.country ? countryNames.find((c) => c.name === data.country).id : null;

		var request = {
			id: document.id,
			fileName: data.fileName,
			description: data.description,
			countryId: countryId,
		};

		const modifyDocument = () =>
			modifyDocumentApi.api().then((w) =>
				w
					.put(request)
					.badRequest((err) => {
						console.log(err);
						var errDetail = JSON.parse(err.text).detail;
						setApiErrors(errDetail);
						throw new Error(errDetail);
					})
					.error(409, (err) => {
						console.log(err);
						var errDetail = JSON.parse(err.text).detail;
						setApiErrors(errDetail);
						throw new Error(errDetail);
					})
					.json((json) => {
						var modifiedDocument = {
							...request,
							country: data.country,
						};

						if (json.hasAccess) {
							dispatch({
								type: "MODIFY_DOCUMENT_DETAILS",
								payload: modifiedDocument,
							});
						} else {
							dispatch({
								type: "REMOVE_DOCUMENT",
								payload: { id: document.id },
							});
						}

						return modifiedDocument;
					})
					.then(() => toastr.success("Document details changed"))
					.then(() => closeDrawer())
					.catch((error) => {
						console.log(error);
						toastr.error("Unable to change Document details.");
					})
					.finally(() => setIsBusy(false))
			);

		setIsBusy(true);
		modifyDocument();
	};

	const closeDrawer = () => {
		history.goBack();
	};

	return (
		<div>
			<form onSubmit={handleSubmit(onSubmit)}>
				<section>
					<TextField
						label="File Name *"
						fullWidth
						name="fileName"
						error={!!(errors && errors.fileName)}
						style={{ marginBottom: 16 }}
						autoComplete="off"
						inputRef={register({
							required: {
								value: true,
								message: "File Name is required",
							},
							maxLength: {
								value: 255,
								message: "File Name is too long",
							},
						})}
						inputProps={{
							"aria-label": "File Name",
						}}
						helperText={errors && errors.fileName && errors.fileName.message}
						defaultValue={document.fileName}
					/>
				</section>

				<section>
					<TextField
						label="Description"
						fullWidth
						name="description"
						multiline
						autoComplete="off"
						rows="6"
						error={!!(errors && errors.description)}
						style={{ marginBottom: 16 }}
						inputRef={register()}
						inputProps={{
							"aria-label": "Document description",
						}}
						helperText={errors && errors.description && errors.description.message}
						defaultValue={document.description}
					/>
				</section>

				<section>
					<Autocomplete
						id="countryAutocomplete"
						options={countryNames && countryNames.map((r) => r.name)}
						getOptionLabel={(option) => option}
						defaultValue={document.country}
						renderInput={(params) => {
							return (
								<TextField
									{...params}
									name="country"
									label={"Country"}
									fullWidth
									inputRef={register({
										required: {
											value: false,
										},
									})}
									error={!!(errors && errors.country)}
									helperText={errors && errors.country && errors.country.message}
									style={{ marginBottom: 16 }}
								/>
							);
						}}
					/>
				</section>

				{!!apiErrors && <BladeValidationDisplay validationErrors={apiErrors} />}

				<div className={classes.drawerBladeToolbar}>
					<Button
						variant="contained"
						color="primary"
						size="medium"
						type="submit"
						className={classes.button}
						endIcon={<SaveIcon />}
						disabled={isBusy}>
						SAVE
					</Button>
				</div>
			</form>
		</div>
	);
};

const RemoveDocument = ({ document }) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const history = useHistory();
	const removeDocumentApi = useProtectApi("document/delete");
	const [isBusy, setIsBusy] = useState(false);
	const [apiErrors, setApiErrors] = useState();

	const closeDrawer = () => {
		history.goBack();
	};

	const removeDocumentClick = () => {
		var request = {
			Id: document.id,
		};

		const apiDelete = () =>
			removeDocumentApi.api().then((w) =>
				w
					.put(request)
					.badRequest((err) => {
						console.log(err);
						var errDetail = JSON.parse(err.text).detail;
						setApiErrors(errDetail);
						throw new Error(errDetail);
					})
					.error(409, (err) => {
						console.log(err);
						var errDetail = JSON.parse(err.text).detail;
						setApiErrors(errDetail);
						throw new Error(errDetail);
					})
					.res(() => {
						dispatch({
							type: "REMOVE_DOCUMENT",
							payload: document,
						});
					})
					.then(() => toastr.success("Document removed"))
					.then(() => closeDrawer())
					.catch((error) => {
						console.log(error);
						toastr.error("Unable to remove Document.");
					})
					.finally(() => setIsBusy(false))
			);

		setIsBusy(true);
		apiDelete();
	};

	return (
		<div>
			<Typography>
				<p>Remove document '{document.fileName}' from the application?</p>
				<p>
					If you do so this Document will no longer be available and the source document will be removed from
					storage.
				</p>
			</Typography>
			<div className={classes.drawerBladeToolbar}>
				<DeleteButton
					variant="outlined"
					color="primary"
					size="medium"
					onClick={() => removeDocumentClick()}
					className={classes.button}
					disabled={isBusy}
					text="Remove"
				/>
			</div>
			{!!apiErrors && <BladeValidationDisplay validationErrors={apiErrors} />}
		</div>
	);
};

export default DocumentManage;
